import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PremiumPopup from '../components/access/PremiumPopup';

const SubscriptionContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const SubscriptionProvider = ({ children }) => {
    const [subscription, setSubscription] = useState({
        plan: 'free',
        isFree: true,
        isPaid: false,
        amount: 0,
        expiry: null,
        planDurationMonths: 0,
        loading: true
    });

    const [eliteSelection, setEliteSelection] = useState(null);
    const [mockInterviewCount, setMockInterviewCount] = useState(0);
    const [isPremiumPopupOpen, setIsPremiumPopupOpen] = useState(false);

    const showPremiumPopup = useCallback(() => setIsPremiumPopupOpen(true), []);
    const hidePremiumPopup = useCallback(() => setIsPremiumPopupOpen(false), []);

    const fetchSubscriptionData = useCallback(async () => {
        let userId = localStorage.getItem('user_id');
        
        // Fallback to 'user' object if 'user_id' is missing
        if (!userId) {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                userId = user?.userId || user?.id;
            } catch (e) {}
        }

        if (!userId) {
            setSubscription(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            const encodedUserId = encodeURIComponent(userId);
            const headers = { 'user-id': userId };
            
            // In a real app, these could be one endpoint or parallel requests
            const [subRes, eliteRes, usageRes] = await Promise.allSettled([
                axios.get(`${API_BASE_URL}/api/skillgenie-premium/status/${encodedUserId}`),
                axios.get(`${API_BASE_URL}/api/elite/selection`, { headers }),
                axios.get(`${API_BASE_URL}/api/usage/mock-interview`, { 
                    headers: { ...headers, 'x-tenant-id': localStorage.getItem('tenant_id') || '' } 
                })
            ]);

            if (subRes.status === 'fulfilled') {
                const subData = subRes.value.data;
                const isPaid = subData.hasActiveSubscription || subData.subscription?.plan === 'college';

                // Calculate plan duration in months based on amount if not provided
                let planDurationMonths = subData.subscription?.planDurationMonths || 0;
                if (!planDurationMonths && subData.subscription?.amount) {
                    const amount = Number(subData.subscription.amount);
                    if (amount >= 14999) planDurationMonths = 6;
                    else if (amount >= 7999) planDurationMonths = 3;
                    else if (amount >= 3999) planDurationMonths = 1;
                }

                setSubscription({
                    plan: subData.subscription?.plan_name || 'free',
                    isFree: !isPaid,
                    isPaid: isPaid,
                    amount: subData.subscription?.amount || 0,
                    expiry: subData.subscription?.end_date || null,
                    daysRemaining: isPaid ? (subData.subscription?.daysRemaining || null) : null,
                    planDurationMonths,
                    loading: false
                });
            } else {
                setSubscription(prev => ({ ...prev, loading: false }));
            }

            if (eliteRes.status === 'fulfilled' && eliteRes.value?.data?.success) {
                setEliteSelection(eliteRes.value.data.selection?.selected_technology || null);
            }

            if (usageRes.status === 'fulfilled' && usageRes.value?.data?.success) {
                setMockInterviewCount(usageRes.value.data.count || 0);
            } else if (usageRes.status === 'rejected') {
                console.error('Usage API rejected:', usageRes.reason);
                // Fallback to local storage tracker if API fails (502/CORS)
                try {
                    const localCount = Number(localStorage.getItem('mock_interview_usage_count') || 0);
                    setMockInterviewCount(localCount);
                } catch (e) {}
            }

        } catch (error) {
            console.error('Error fetching subscription data:', error);
            setSubscription(prev => ({ ...prev, loading: false }));
        }
    }, []);

    useEffect(() => {
        fetchSubscriptionData();
        // Add event listener for storage changes (login/logout)
        window.addEventListener('storage', fetchSubscriptionData);
        return () => window.removeEventListener('storage', fetchSubscriptionData);
    }, [fetchSubscriptionData]);

    const canAccessFeature = useCallback((feature, context = {}) => {
        if (subscription.loading) return true;

        // College/tenant users have full access
        const host = typeof window !== 'undefined' ? (window.location?.hostname || '') : '';
        const isTenantStudent = (host.endsWith('.theskillgenie.com') && !host.startsWith('www.')) || 
                                (host !== 'localhost' && !host.includes('theskillgenie.com'));

        // Check if user is a college student from localStorage
        const storedUser = localStorage.getItem("user");
        let isCollegeUser = false;
        try {
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                isCollegeUser = (parsedUser?.role === 'student' || parsedUser?.role === 'tenant-student') && 
                               (parsedUser?.tenant || parsedUser?.college_id || parsedUser?.institution_id);
            }
        } catch (e) {}

        if (isTenantStudent || isCollegeUser || subscription.isPaid || (storedUser && JSON.parse(storedUser)?.role === 'admin')) {
            return true; // All paid users (1/3/6 months) and tenant users have full access including TSG, Mock Interview, and Bootcamp
        }

        // Free User Rules
        switch (feature) {
            case 'TECH_EXPLORER_MCQ':
            case 'LAUNCHPAD_MCQ':
                return context.level <= 5;

            case 'TECH_EXPLORER_DOCS':
            case 'LAUNCHPAD_DOCS':
                return context.docIndex < 2;

            case 'TSG_NOTES':
            case 'TSG_AI':
                return false; // Strictly restricted for non-paid users

            case 'MOCK_INTERVIEW':
                // For learners: Accessed once before payment.
                // If they've used ANY mock interview before payment, they need to pay
                return mockInterviewCount < 1;

            case 'BOOTCAMP':
                return false; // Strictly restricted for non-paid

            case 'ELITE_TECH':
                return eliteSelection === context.technology;

            case 'ELITE_DOCS':
                return context.docIndex < 2;

            default:
                return false;
        }
    }, [subscription, eliteSelection, mockInterviewCount]);

    return (
        <SubscriptionContext.Provider value={{ 
            subscription, 
            eliteSelection, 
            mockInterviewCount, 
            canAccessFeature, 
            refreshSubscription: fetchSubscriptionData,
            showPremiumPopup,
            hidePremiumPopup
        }}>
            {children}
            <PremiumPopup 
                isOpen={isPremiumPopupOpen} 
                onClose={hidePremiumPopup} 
            />
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};
